import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES, QUIZ_RUBRIC_PASS_POINTS, QUIZ_HINT_COST_POINTS, QUIZ_RUBRIC_MAX_POINTS, QUIZ_GAME_MAX_DELTA } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { quizOptionsAreWeighted, rubricPointsToScoreDelta, formatRubricAsGamePointsLabel } from "../../utils/quizScoring";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaOrange.png";
import "../StartGame.css";
import "./Scenario2.css";

/** Clases extra del popup SweetAlert2 (tema burdeos Cobega). */
const SC2_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--fepol";
/** Pista: borde cian, avatar enmarcado, botón confirmar en teal (ver modals.css · modal-sg5--hint-sc2). */
const SC2_HINT_THEME_CLASS = `${SC2_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();

const SC2_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM = "¿Cuál debería ser el primer paso del comité?";

const RETROALIMENTACION =
	"Antes de adoptar herramientas de IA, el comité debe diagnosticar el contexto: casos de uso concretos, procesos implicados, datos disponibles, riesgos y objetivos de negocio. Sin ese marco, las licencias, los experimentos aislados o los anuncios simbólicos no garantizan una transformación útil ni responsable.";

const HINT_HTML =
	'<div class="txtTexto1 mb-2">Pensad en la IA como una decisión de transformación, no solo como una compra tecnológica. Antes de elegir herramientas, conviene saber qué problema se quiere resolver, con qué datos y con qué riesgos.</div>';

/** Opciones con `points` (rúbrica 0–100): una sola elección, sin reintentos por «incorrecta». */
const SCENARIO2_QUIZ1_BASE_OPTIONS = [
	{
		id: 1,
		label: "A",
		text: "Comprar licencias de una herramienta de IA para toda la plantilla",
		points: 0,
		img: null,
		explanation: "",
	},
	{
		id: 2,
		label: "B",
		text: "Identificar casos de uso, procesos afectados, datos disponibles, riesgos y objetivos de negocio",
		points: 100,
		img: null,
		explanation: "",
	},
	{
		id: 3,
		label: "C",
		text: "Permitir que cada departamento experimente libremente y revisar resultados más adelante",
		points: 50,
		img: null,
		explanation: "",
	},
	{
		id: 4,
		label: "D",
		text: "Lanzar una comunicación interna anunciando que la empresa ya es “AI-first”",
		points: 0,
		img: null,
		explanation: "",
	},
];

const IS_WEIGHTED_QUIZ = quizOptionsAreWeighted(SCENARIO2_QUIZ1_BASE_OPTIONS);

function Scenario2Quiz1() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ1;
	const SCENARIO_ID = SCENARIOS.SCENARIO_2;

	const [selectedOption, setSelectedOption] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	/** Capital al entrar en este quiz; el API hace $inc, hay que enviar solo la delta de esta pregunta. */
	const capitalAtQuizStartRef = useRef(null);

	useEffect(() => {
		// Asegurar que el score base sea el acumulado del grupo (incluye gastos como comodines),
		// incluso si llega asíncrono tras rehidratar el grupo.
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -2;

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "orange");

	const displayOptions = useMemo(() => shuffleQuizOptions(SCENARIO2_QUIZ1_BASE_OPTIONS), []);

	async function submitAnswer(finalScore, correct, extra = {}) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO2_CONTEXT2,
				correct,
				...extra,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO2_CONTEXT2);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO2_CONTEXT2);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		if (hintUsed) {
			await modalHintShow({
				hint: HINT_HTML,
				hintMarkHtml: SC2_HINT_MARK_HTML,
				color: "orange",
				big: false,
				themePopupClass: SC2_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			imageHintUsed,
			hint: HINT_HTML,
			hintMarkHtml: SC2_HINT_MARK_HTML,
			color: "orange",
			big: false,
			themePopupClass: SC2_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(QUIZ_HINT_COST_POINTS);
		}
	};

	function checkAnswer() {
		if (!selectedOption) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 orange">Debes seleccionar una opción</div>`,
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});
			return;
		}

		const option = displayOptions.find((o) => o.id === selectedOption);
		if (!option) return;

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "orange",
			themePopupClass: SC2_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			if (IS_WEIGHTED_QUIZ) {
				const rubricPass = option.points >= QUIZ_RUBRIC_PASS_POINTS;
				const scoreDelta = rubricPointsToScoreDelta(option.points, QUIZ_RUBRIC_MAX_POINTS, QUIZ_GAME_MAX_DELTA);
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = scoreDelta - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				const avatarUrl = rubricPass ? getRandomAciertoAvatar() : getRandomErrorAvatar();
				const outcomeStyle = rubricPass ? "correct" : "incorrect";

				modalQuestionResolution({
					correct: rubricPass,
					question: `${option.label}. ${option.text}`,
					textHtml: `${buildQuizResultAvatarHtml(avatarUrl, outcomeStyle, "orange")}
                      <div class="txt28 bold pt-4">${formatRubricAsGamePointsLabel(option.points)}</div>
                      <div class="txt22 mt-2">${rubricPass ? "Has acertado." : "No has acertado."}</div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "orange",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				}).then(() => {
					submitAnswer(newScore, rubricPass, { rubricPoints: option.points });
				});

				return;
			}

			if (option.correct) {
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = CORRECT_ANSWER_POINTS - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;

				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				const capitalInicio = capitalAtQuizStartRef.current ?? 0;
				const netoEnEstaPregunta = newScore - capitalInicio;

				modalQuestionResolution({
					correct: true,
					question: `${option.label}. ${option.text}`,
					textHtml: `${buildQuizResultAvatarHtml(getRandomAciertoAvatar(), "correct", "orange")}
                      <div class="txt28 bold pt-4">Has ganado ${attemptDelta} pt en esta pregunta${hintThisAttempt ? ` (−${hintThisAttempt} pt pista)` : ""}</div>
                      <div class="txt20 mt-2">Puntos en esta pregunta: <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt</div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "orange",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				}).then(() => {
					submitAnswer(newScore, true);
				});

				return;
			}

			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
			const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
			const newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			const capitalInicio = capitalAtQuizStartRef.current ?? 0;
			const netoEnEstaPregunta = newScore - capitalInicio;

			modalQuestionResolution({
				correct: false,
				question: `${option.label}. ${option.text}`,
				textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "orange")}
                    <div class="txt28 bold pt-3">Respuesta incorrecta: ${attemptDelta} pt ${hintThisAttempt ? `(incluye −${hintThisAttempt} pt por pista)` : ""}</div>
                    <div class="txt20 mt-2">Puntos en esta pregunta: <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt</div>
                    ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "orange",
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand">
			<div className="start-game-inner start-game-inner--prep5">
				<span className="start-game-badge sg5-badge">
					<span className="start-game-badge-dot" aria-hidden />
					Escenario 1 · Diagnóstico
				</span>

				<div className="sg5-scroll">
					<header className="sg5-hero">
						<h1 className="sg5-title">Pregunta</h1>
						<p className="sg5-lead sc2-quiz-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>
								{IS_WEIGHTED_QUIZ
									? "Elige la opción que mejor refleje el criterio profesional y pulsa Comprobar respuesta. Cada opción suma una puntuación distinta. La pista resta "
									: "Selecciona una opción y pulsa Comprobar respuesta. La pista resta "}
								{QUIZ_HINT_COST_POINTS} pt al marcador.
							</span>
						</p>
					</div>

					<div className="sc2-quiz-options">
						{displayOptions.map((option) => {
							const isSelected = selectedOption === option.id;
							return (
								<button
									key={option.id}
									type="button"
									className={`sg5-aside-card sc2-quiz-option text-start ${isSelected ? "sc2-quiz-option--selected" : ""}`}
									onClick={() => setSelectedOption(option.id)}
									aria-pressed={isSelected ? "true" : "false"}
									aria-label={`Opción ${option.label}`}
								>
									<div className="sc2-quiz-option-row">
										<span className="sc2-quiz-option-letter">{option.label}</span>
										<span className="sc2-quiz-option-text">{option.text}</span>
									</div>
								</button>
							);
						})}
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={
								hintUsed
									? `Pista usada (−${QUIZ_HINT_COST_POINTS} pt)`
									: `Usar pista (−${QUIZ_HINT_COST_POINTS} pt)`
							}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? (
							<span className="badge-penalty sc2-quiz-hint-badge">−{QUIZ_HINT_COST_POINTS} pt</span>
						) : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default Scenario2Quiz1;
